import {
  getAppsV3,
  installApp as installAppRequest,
  uninstallApp as uninstallAppRequest,
  logUpgradeButtonClicked as logUpgradeButtonClickedRequest,
} from '@/modules/app/services/appsService';
import { APP_TYPE, ASSIGNMENT_TYPE, MARKET_EXCLUDED_CODES } from '@/modules/app/constants/appConstants';
import embeddedAppStore from '@/modules/app/store/embeddedAppStore';
import ANGULAR_EMBEDDED_APPS from './angularEmbeddedApps';

// Flags apps that render embedded inside POV (vs the Angular iframe).
const markMigratedApps = (apps, forceMigrated) => apps.map((item) => {
  if (forceMigrated || (item.app_code_name && !ANGULAR_EMBEDDED_APPS.includes(item.app_code_name))) {
    return { ...item, migrated: true };
  }

  return item;
});

// Dedup of the in-flight v3 fetch; kept out of Vuex state (a Promise shouldn't be reactive/serialized).
let inFlightAppsPromise = null;

const state = {
  apps: [],
  loaded: false,
  subscribableApps: [],
  subscribableAppsLoaded: false,
};

const getters = {
  appsLoaded: (state) => state.loaded,
  appsByCodeName: (state) => new Map(state.apps.filter(Boolean).map((app) => [app.app_code_name, app])),
  appByCodeName: (state, storeGetters) => (appCodeName) => storeGetters.appsByCodeName.get(appCodeName),
  installedApps: (state) => state.apps.filter((app) => app.status === 'installed'),
  marketApps: (state) => state.apps.filter((app) => !app.app_types?.includes(APP_TYPE.BILLING)
    && !app.assignment_types?.includes(ASSIGNMENT_TYPE.INTERNAL)
    && !MARKET_EXCLUDED_CODES.includes(app.app_code_name)),
  appsByType: (state) => (type) => state.apps.filter((app) => app.app_types?.includes(type)),
  // TODO (gap §6.1): per-staff menu subitem permission filtering.
  menuApps: (state) => state.apps.filter((app) => (app.assignment_types?.includes(ASSIGNMENT_TYPE.INSTALLED)
    || app.assignment_types?.includes(ASSIGNMENT_TYPE.INTERNAL)) && app.menu_items),
  // v2 get_external_applications parity: upgrade-requiring apps count as external for owners only
  externalAppByCodeName: (state, storeGetters, rootState, rootGetters) => (appCodeName) => {
    const app = storeGetters.appsByCodeName.get(appCodeName);
    if (!app) return undefined;
    if (!app.requires_upgrade) return app;
    return rootGetters['StaffStore/staffData']?.role === 'owner' ? app : undefined;
  },
  isAppInstalled: (state) => (appCodeName) => state.apps.some(
    (app) => app.app_code_name === appCodeName && app.status === 'installed',
  ),
  // Effective install: explicitly installed OR entitled via package/offering
  // (assignment_type `installed` ∪ `internal`), EXCLUDING marketplace-offered-only
  // (`directory_offering`). Mirrors the webaiwidgetserver install gate; use this
  // (not `isAppInstalled`, which is `status==='installed'` only) when a
  // package-entitled business must count as installed.
  isAppInstalledOrEntitled: (state) => (appCodeName) => state.apps.some(
    (app) => app.app_code_name === appCodeName
      && (app.assignment_types?.includes(ASSIGNMENT_TYPE.INSTALLED)
        || app.assignment_types?.includes(ASSIGNMENT_TYPE.INTERNAL)),
  ),
  isAppAvailable: (state, storeGetters) => (appCodeName) => !!storeGetters.appByCodeName(appCodeName),
  billingApp: (state, storeGetters) => storeGetters.appsByType(APP_TYPE.BILLING)[0],
  // subscribable apps: v3 market slice + paid_data (populated by the getSubscribableApps action)
  subscribableApps: (state) => state.subscribableApps,
  getSubscribableAppByCodeName: (state) => (appCodeName) => state.subscribableApps.find((app) => app.app_code_name === appCodeName),
  purchasedApps: (state) => state.subscribableApps.filter((app) => app.paid_data && !app.paid_data.requires_purchase),
};

const mutations = {
  setApps(state, apps) {
    state.apps = apps;
  },
  setLoaded(state, loaded) {
    state.loaded = loaded;
  },
  setSubscribableApps(state, apps) {
    state.subscribableApps = apps;
  },
  setSubscribableAppsLoaded(state, loaded) {
    state.subscribableAppsLoaded = loaded;
  },
};

const actions = {
  async loadApps({ state, commit, rootGetters }, { force = false } = {}) {
    if (state.loaded && !force) return state.apps;
    if (inFlightAppsPromise && !force) return inFlightAppsPromise;

    const promise = (async () => {
      try {
        const apps = await getAppsV3();
        const forceEmbedded = rootGetters['BusinessStore/hasFeature']('force_embedded_apps_in_POV');
        commit('setApps', markMigratedApps(apps, forceEmbedded));
        commit('setLoaded', true);
        return state.apps;
      } catch (error) {
        console.error('Error loading apps', error);
        return [];
      } finally {
        // Only clear if a newer (e.g. forced) fetch hasn't already replaced this one.
        if (inFlightAppsPromise === promise) inFlightAppsPromise = null;
      }
    })();

    inFlightAppsPromise = promise;
    return promise;
  },
  refreshApps({ dispatch }) {
    return dispatch('loadApps', { force: true });
  },
  async loadBillingAppData({ dispatch }) {
    await dispatch('loadApps');
  },
  async installApp({ dispatch, rootGetters }, appCodeName) {
    const result = await installAppRequest({
      business_uid: rootGetters['AuthStore/businessUid'],
      app_code_name: appCodeName,
    });
    if (result?.code !== 'error') await dispatch('refreshAppsAfterStatusChange');
    return result;
  },
  async uninstallApp({ dispatch, rootGetters }, appCodeName) {
    const result = await uninstallAppRequest({
      business_uid: rootGetters['AuthStore/businessUid'],
      app_code_name: appCodeName,
    });
    if (result?.code !== 'error') await dispatch('refreshAppsAfterStatusChange');
    return result;
  },
  // Subscriptions are refreshed once here; getSubscribableApps refetches the catalog and the market rebuilds on top.
  async refreshAppsAfterStatusChange({ dispatch }) {
    try {
      await dispatch('SubscriptionStore/fetchActiveSubscriptions', { force: true }, { root: true });
      await dispatch('getSubscribableApps', { force: true });
      await Promise.all([
        dispatch('AppMarketStore/fetchMarketApps', { force: true }, { root: true }),
        dispatch('MainMenuStore/fetchApplicationsMenuItemsForce', null, { root: true }),
      ]);
    } catch (error) {
      console.error('Failed to refresh apps after a status change', error);
    }
  },
  logUpgradeButtonClicked(_, appCodeName) {
    return logUpgradeButtonClickedRequest(appCodeName);
  },
  async getSubscribableApps({
    state, getters: storeGetters, commit, rootGetters, dispatch,
  }, { force = false } = {}) {
    if (state.subscribableApps.length && !force) return;

    try {
      await dispatch('loadApps', { force });
      // Loads geoData if absent; findUserPrice throws without it, losing the whole slice.
      await dispatch('AccountStore/getAccountCurrency', null, { root: true });
      const geoData = rootGetters['AccountStore/geoData'];
      // Clone the market slice: aggregateAppData mutates its input, and marketApps returns state.apps refs.
      const subscribableApps = await dispatch(
        'SubscriptionStore/aggregateAppData',
        { apps: storeGetters.marketApps.map((app) => ({ ...app })), geoData },
        { root: true },
      );
      commit('setSubscribableApps', subscribableApps);
      commit('setSubscribableAppsLoaded', true);
    } catch (error) {
      console.error('Failed to build subscribable apps', error);
      commit('setSubscribableApps', []);
      commit('setSubscribableAppsLoaded', false);
    }
  },
};

const modules = {
  EmbeddedAppStore: embeddedAppStore,
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules,
};
